import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, UploadCloud, Save, Link as LinkIcon, Youtube, Video } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BLANK = {
  title: "", description: "", sport: "", player: "",
  order: 0, active: true,
  source_url: "", storage_path: "", thumbnail_url: "",
};

export default function AdminBigHits() {
  const [hits, setHits] = useState([]);
  const [editing, setEditing] = useState(null);  // id or "new"
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setHits((await api.get("/admin/big-hits")).data); }
    catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setForm(BLANK); };
  const openEdit = (h) => {
    setEditing(h.id);
    setForm({
      title: h.title, description: h.description || "",
      sport: h.sport || "", player: h.player || "",
      order: h.order || 0, active: h.active !== false,
      source_url: h.source_type === "upload" ? "" : (h.link_url || ""),
      storage_path: h.storage_path || "",
      thumbnail_url: h.thumbnail_url || "",
    });
  };
  const cancel = () => { setEditing(null); setForm(BLANK); };

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/big-hits/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((cur) => ({ ...cur, storage_path: r.data.storage_path, source_url: "" }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.source_url.trim() && !form.storage_path.trim()) {
      return toast.error("Provide a YouTube/TikTok/Whatnot URL or upload a video file");
    }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await api.post("/admin/big-hits", payload);
      else await api.put(`/admin/big-hits/${editing}`, payload);
      toast.success(editing === "new" ? "Big Hit added" : "Updated");
      cancel();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this Big Hit permanently?")) return;
    try {
      await api.delete(`/admin/big-hits/${id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="font-display text-4xl">Big Hits Gallery</h1>
          <p className="text-zinc-400 text-sm mt-1">Paste a YouTube/TikTok/Whatnot URL or upload an MP4/MOV clip.</p>
        </div>
        {editing === null && (
          <button onClick={openNew} className="btn-primary text-sm" data-testid="big-hit-new">
            <Plus size={14} className="inline mr-2 -mt-0.5"/> Add Big Hit
          </button>
        )}
      </div>

      {editing !== null && (
        <div className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-8 space-y-4" data-testid="big-hit-editor">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *" value={form.title} onChange={(v) => upd("title", v)} testid="bh-title" />
            <Field label="Display order (lower = first)" type="number" value={form.order} onChange={(v) => upd("order", v)} testid="bh-order" />
            <Field label="Player" value={form.player} onChange={(v) => upd("player", v)} testid="bh-player" />
            <Field label="Sport" value={form.sport} onChange={(v) => upd("sport", v)} testid="bh-sport" />
          </div>
          <div>
            <label className="text-label text-zinc-500">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)}
                      data-testid="bh-description" className="input mt-1" />
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="text-label text-zinc-500 mb-3">Source · pick one</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 flex items-center gap-1 mb-1"><LinkIcon size={12}/> URL</label>
                <input value={form.source_url}
                       onChange={(e) => {
                         const v = e.target.value;
                         // Single functional update — two upd() calls would drop
                         // one field because both spread the same stale `form`.
                         setForm((cur) => ({
                           ...cur,
                           source_url: v,
                           storage_path: v ? "" : cur.storage_path,
                         }));
                       }}
                       placeholder="youtube.com/watch?v=… · tiktok.com/@… · whatnot.com/…"
                       data-testid="bh-source-url" className="input" />
                {form.source_url && !form.storage_path && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Detected: <span className="text-[var(--cl-orange)]">{detectPlatform(form.source_url)}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-zinc-400 flex items-center gap-1 mb-1"><UploadCloud size={12}/> Upload MP4/MOV/WebM (max 100MB)</label>
                <label className="input cursor-pointer flex items-center justify-center gap-2"
                       data-testid="bh-upload-btn">
                  <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={upload} />
                  {uploading ? "Uploading…" : form.storage_path ? "Replace video" : "Choose file"}
                </label>
                {form.storage_path && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Uploaded: <span className="font-mono text-[var(--cl-orange)]">{form.storage_path.split("/").pop()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 border-t border-white/10 pt-4">
            <Field label="Thumbnail URL (optional)" value={form.thumbnail_url} onChange={(v) => upd("thumbnail_url", v)} testid="bh-thumbnail" />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)}
                     data-testid="bh-active" />
              <span className="text-sm">Active (visible on public gallery)</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="bh-save">
              <Save size={14} className="inline mr-2 -mt-0.5" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={cancel} className="btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="big-hit-list">
        {hits.length === 0 && !editing && <div className="text-zinc-500 text-sm col-span-full">No big hits yet.</div>}
        {hits.map((h) => (
          <div key={h.id} className={`border rounded overflow-hidden ${h.active ? "border-white/10" : "border-white/5 opacity-50"}`}
               data-testid={`big-hit-row-${h.id}`}>
            <div className="aspect-video bg-black relative">
              {h.thumbnail_url
                ? <img src={h.thumbnail_url} alt={h.title} className="w-full h-full object-cover" />
                : h.source_type === "upload"
                  ? <video muted preload="metadata" src={`${BACKEND_URL}${h.embed_url}#t=0.1`} className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-[#2a0808] to-black flex items-center justify-center"><PlatformIcon type={h.source_type} /></div>}
              <span className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-1 rounded text-[10px] uppercase tracking-widest text-[var(--cl-orange)]">
                {h.source_type}
              </span>
              {!h.active && <span className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-[10px] uppercase tracking-widest">Hidden</span>}
            </div>
            <div className="p-4 bg-[var(--cl-surface)]">
              <div className="font-medium truncate">{h.title}</div>
              <div className="text-xs text-zinc-500 mt-1">Order: {h.order} · {h.player || "—"}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(h)} className="btn-outline text-xs" data-testid={`bh-edit-${h.id}`}>Edit</button>
                <button onClick={() => del(h.id)} className="text-zinc-500 hover:text-[var(--cl-primary)] text-xs flex items-center gap-1" data-testid={`bh-delete-${h.id}`}>
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-label text-zinc-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="input mt-1" />
    </div>
  );
}

function PlatformIcon({ type }) {
  if (type === "youtube") return <Youtube size={32} className="text-[var(--cl-orange)]" />;
  if (type === "upload") return <Video size={32} className="text-[var(--cl-orange)]" />;
  return <LinkIcon size={32} className="text-[var(--cl-orange)]" />;
}

function detectPlatform(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "YouTube";
  if (u.includes("tiktok.com")) return "TikTok";
  if (u.includes("whatnot.com")) return "Whatnot (link-only)";
  return "External (link-only)";
}
